import { useCallback, useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Route, Routes } from 'react-router-dom';

import { callApi } from './api';
import { Login } from './components/Login';
import { Layout } from './components/Layout';
import { SessionContext } from './SessionContext';

import { Overview } from './pages/Overview';
import { Imports } from './pages/Imports';
import { Users } from './pages/Users';
import { UserDetail } from './pages/UserDetail';
import { Feedback } from './pages/Feedback';
import { Waitlist } from './pages/Waitlist';
import { Invites } from './pages/Invites';
import { SystemHealth } from './pages/SystemHealth';
import { OperationsRunbook } from './pages/OperationsRunbook';
import { Search } from './pages/Search';
import { UnmatchedObservations } from './pages/UnmatchedObservations';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const supabase = SUPABASE_URL && SUPABASE_ANON_KEY
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
  : null;

// Boot phases:
//   booting      — checking persisted Supabase session
//   error        — env vars missing or /admin/me threw
//   no_session   — show Login
//   checking     — have session, calling /admin/me
//   not_admin    — signed in but not on the allow-list
//   ready        — render the panel
function useBoot() {
  const [phase, setPhase] = useState({ kind: 'booting' });

  const setSession = useCallback((session) => {
    if (!session) {
      setPhase({ kind: 'no_session' });
      return;
    }
    setPhase({ kind: 'checking', session });
    callApi('/api/v1/admin/me', { accessToken: session.access_token })
      .then((me) =>
        setPhase(
          me.is_admin
            ? { kind: 'ready', session, me }
            : { kind: 'not_admin', session }
        )
      )
      .catch((e) => setPhase({ kind: 'error', message: e.message }));
  }, []);

  useEffect(() => {
    if (!supabase) {
      setPhase({ kind: 'error', message: 'Supabase env vars not configured.' });
      return undefined;
    }
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, [setSession]);

  return [phase, setSession];
}

export default function AdminApp() {
  const [phase, setSession] = useBoot();

  const signOut = async () => {
    if (supabase) await supabase.auth.signOut();
    setSession(null);
  };

  switch (phase.kind) {
    case 'error':
      return (
        <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
          <p className="text-red-700 text-sm">{phase.message}</p>
        </div>
      );
    case 'no_session':
      return <Login supabase={supabase} onSession={setSession} />;
    case 'checking':
    case 'booting':
      return <div className="p-6 text-stone-600">Checking access…</div>;
    case 'not_admin':
      return (
        <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
          <div className="bg-white rounded-2xl shadow p-6 max-w-sm text-center space-y-3">
            <h1 className="text-lg font-semibold text-stone-800">Not authorized</h1>
            <p className="text-sm text-stone-600">
              This account isn't on the admin allow-list.
            </p>
            <button onClick={signOut} className="text-sm text-stone-500 underline">
              Sign out
            </button>
          </div>
        </div>
      );
    case 'ready':
      return (
        <ReadyShell session={phase.session} me={phase.me} signOut={signOut} />
      );
    default:
      return null;
  }
}

const EXCLUDE_ADMINS_KEY = 'admin.excludeAdmins';

function ReadyShell({ session, me, signOut }) {
  const [counts, setCounts] = useState({});
  // Global filter — read once from localStorage; persists across reloads.
  // Pages that respect it (currently Search) read this from context and
  // append `&exclude_admins=true` to their fetches.
  const [excludeAdmins, setExcludeAdminsState] = useState(() => {
    try { return localStorage.getItem(EXCLUDE_ADMINS_KEY) === 'true'; }
    catch { return false; }
  });
  const setExcludeAdmins = (value) => {
    setExcludeAdminsState(value);
    try { localStorage.setItem(EXCLUDE_ADMINS_KEY, String(value)); } catch { /* ignore */ }
  };
  const value = {
    session, me, signOut, counts, setCounts, excludeAdmins, setExcludeAdmins,
  };
  return (
    <SessionContext.Provider value={value}>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Overview />} />
          <Route path="imports" element={<Imports />} />
          <Route path="beli" element={<Imports />} />
          <Route path="google" element={<Imports />} />
          <Route path="users" element={<Users />} />
          <Route path="users/:id" element={<UserDetail />} />
          <Route path="feedback" element={<Feedback />} />
          <Route path="waitlist" element={<Waitlist />} />
          <Route path="invites" element={<Invites />} />
          <Route path="system" element={<SystemHealth />} />
          <Route path="system/crons" element={<OperationsRunbook />} />
          <Route path="search" element={<Search />} />
          <Route path="imports/unmatched" element={<UnmatchedObservations />} />
        </Route>
      </Routes>
    </SessionContext.Provider>
  );
}
